import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ceoApi } from "@/user/ceo/ceoApi";
import { ActionIcon } from "@/user/workspace/wsui";
import { ACTION_LABEL, PRIMARY_ACTIONS, DANGER_ACTIONS, STATE_STYLE, PRIORITY_STYLE, prettyState, CARD_ICON } from "@/user/ceo/cards.config";
import { MoneyMeterDelta } from "@/user/ceo/MoneyMeter";

export const SCAN_STAGES = [
    "Uploading file…", "Reading document…", "Extracting business data…",
    "Detecting risks…", "Matching department context…",
    "Generating executive insights…", "Preparing Decision Cards…",
];

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.csv,.txt,.xlsx,.xls,.docx";

export default function BusinessScan() {
    const nav = useNavigate();
    const [dragging, setDragging] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [stage, setStage] = useState(0);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const inputRef = useRef(null);

    const loadHistory = useCallback(() => {
        ceoApi.scan.list().then((r) => setHistory(r.scans || [])).catch(() => {});
    }, []);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    useEffect(() => {
        if (!scanning) return;
        setStage(0);
        const iv = setInterval(() => setStage((s) => Math.min(s + 1, SCAN_STAGES.length - 1)), 1600);
        return () => clearInterval(iv);
    }, [scanning]);

    async function runScan(file) {
        if (!file) return;
        setResult(null);
        setPreview(file.type?.startsWith("image/") ? URL.createObjectURL(file) : null);
        setScanning(true);
        try {
            const data = await ceoApi.scan.upload(file);
            setResult(data);
            toast.success("Scan complete");
            loadHistory();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Scan failed. Try a clearer image or a PDF.");
        } finally {
            setScanning(false);
            setPreview(null);
        }
    }

    function onDrop(e) {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f) runScan(f);
    }

    async function openScan(id) {
        try {
            const d = await ceoApi.scan.get(id);
            setResult(d);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch { toast.error("Could not open scan"); }
    }
    async function delScan(id) {
        try { await ceoApi.scan.del(id); loadHistory(); toast.success("Scan deleted"); }
        catch { toast.error("Delete failed"); }
    }
    async function cardAction(card, action) {
        try {
            const updated = await ceoApi.cardAction(card.id, action);
            toast.success(`${ACTION_LABEL[action] || action} recorded`);
            setResult((r) => r ? { ...r, decision_cards: (r.decision_cards || []).map((c) => c.id === card.id ? updated : c) } : r);
        } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    }

    return (
        <div className="max-w-6xl mx-auto" data-testid="business-scan-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-violet-600">Gemini Vision</div>
                    <h1 className="mt-1 font-display text-3xl sm:text-4xl font-light tracking-tight text-black">Business Scan AI</h1>
                    <p className="mt-2 text-[14px] text-neutral-500 max-w-xl">Upload any invoice, contract, report, dashboard, whiteboard or warehouse photo. I'll extract the data, detect risks, and generate executive Decision Cards.</p>
                </div>
                <button onClick={() => nav("/app/chat")} data-testid="scan-goto-chat" className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-full bg-white border border-black/10 hover:bg-neutral-50">
                    <ActionIcon name="chart" /> Back to Chat
                </button>
            </div>

            {/* Upload / scanning */}
            {!scanning && (
                <div
                    data-testid="scan-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => inputRef.current?.click()}
                    className={`mt-8 rounded-3xl border-2 border-dashed p-12 text-center cursor-pointer transition-all ${dragging ? "border-violet-400 bg-violet-50/60" : "border-black/10 bg-white/60 hover:border-violet-300 hover:bg-violet-50/30"}`}
                >
                    <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" data-testid="scan-file-input"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) runScan(f); }} />
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-violet-50 text-violet-600 grid place-items-center">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 16V4m0 0L8 8m4-4l4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                    <div className="mt-4 font-display text-xl text-black">Drop a file or click to upload</div>
                    <div className="mt-1.5 text-[12.5px] text-neutral-400">PDF · Images · Excel · CSV · Word — up to 10 MB</div>
                </div>
            )}

            {scanning && <ScanAnimation stage={stage} preview={preview} />}

            {/* Result */}
            {result && !scanning && <ScanResult data={result} onCardAction={cardAction} />}

            {/* History */}
            <div className="mt-14" data-testid="scan-history">
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl font-light text-black">Scan History</h2>
                    <span className="text-[12px] text-neutral-400">{history.length} scan{history.length === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-4 space-y-2">
                    {history.length === 0 && <div className="glass rounded-2xl p-6 text-[13.5px] text-neutral-400 text-center">No scans yet. Upload a document to get started.</div>}
                    {history.map((s) => (
                        <div key={s.id} data-testid={`scan-row-${s.id}`} className="glass rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap">
                            <span className="h-9 w-9 rounded-xl bg-violet-50 text-violet-600 grid place-items-center shrink-0"><ActionIcon name="file" /></span>
                            <div className="min-w-0 flex-1">
                                <div className="text-[13.5px] font-medium text-black truncate">{s.file_name}</div>
                                <div className="text-[11.5px] text-neutral-400 truncate">
                                    {(s.document_type || "document").replace(/_/g, " ")} · {(s.department_detected || []).join(", ") || "—"} · {new Date(s.created_at).toLocaleString()}
                                </div>
                            </div>
                            <span className="text-[11px] rounded-full px-2.5 py-1 bg-neutral-100 text-neutral-600">{s.confidence_score ?? 0}% conf</span>
                            <span className="text-[11px] rounded-full px-2.5 py-1 bg-violet-50 text-violet-700">{s.cards_count ?? 0} card{(s.cards_count ?? 0) === 1 ? "" : "s"}</span>
                            <button onClick={() => openScan(s.id)} data-testid={`scan-open-${s.id}`} className="text-[12.5px] font-medium px-3 py-1.5 rounded-full bg-black text-white hover:bg-neutral-800">Open</button>
                            <button onClick={() => delScan(s.id)} data-testid={`scan-delete-${s.id}`} className="text-[12.5px] px-3 py-1.5 rounded-full text-rose-600 border border-rose-200 hover:bg-rose-50">Delete</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function ScanAnimation({ stage, preview }) {
    return (
        <div className="mt-8 rounded-3xl glass-strong p-8 relative overflow-hidden" data-testid="scan-animation">
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 60% at 50% 0%, rgba(139,92,246,0.14), transparent 70%)" }} />
            <div className="relative flex flex-col items-center">
                <div className="relative h-52 w-40 rounded-2xl bg-white border border-black/10 overflow-hidden shadow-[0_20px_60px_-25px_rgba(139,92,246,0.5)]">
                    {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> :
                        <div className="h-full w-full grid place-items-center text-neutral-300"><svg width="40" height="40" viewBox="0 0 24 24" fill="none"><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z" stroke="currentColor" strokeWidth="1.4" /></svg></div>}
                    <div className="scan-beam absolute left-0 right-0 h-16" />
                </div>
                <div className="mt-6 h-6 text-[15px] font-medium text-violet-700" data-testid="scan-stage">{SCAN_STAGES[stage]}</div>
                <div className="mt-3 w-full max-w-sm h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${((stage + 1) / SCAN_STAGES.length) * 100}%`, background: "linear-gradient(90deg,#a78bfa,#6366f1)" }} />
                </div>
                <div className="mt-2 text-[11.5px] text-neutral-400">Powered by CEO's GPT · Gemini Vision</div>
            </div>
            <style>{`.scan-beam{background:linear-gradient(180deg,transparent,rgba(139,92,246,0.55),transparent);animation:scanmove 1.8s ease-in-out infinite;}@keyframes scanmove{0%{top:-20%}100%{top:110%}}`}</style>
        </div>
    );
}

export function ScanResult({ data, onCardAction }) {
    const ed = data.extracted_data && typeof data.extracted_data === "object" ? Object.entries(data.extracted_data) : [];
    const money = data.money_impact && typeof data.money_impact === "object"
        ? Object.entries(data.money_impact).filter(([k, v]) => k !== "is_estimate" && v !== "" && v != null) : [];
    const risks = Array.isArray(data.risks) ? data.risks : [];
    const tasks = Array.isArray(data.tasks) ? data.tasks : [];
    const conf = data.confidence_score ?? 0;
    const confColor = conf >= 70 ? "bg-emerald-500" : conf >= 50 ? "bg-amber-500" : "bg-rose-500";

    return (
        <div className="mt-8 space-y-5" data-testid="scan-result">
            <div className="glass-strong rounded-3xl p-6">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-600 bg-violet-50 rounded-full px-2.5 py-1">{(data.document_type || "document").replace(/_/g, " ")}</span>
                    {(data.department_detected || []).map((d) => <span key={d} className="text-[11px] rounded-full px-2.5 py-1 bg-neutral-100 text-neutral-700">{d}</span>)}
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white border border-black/[0.07] rounded-full px-2.5 py-1"><span className={`h-1.5 w-1.5 rounded-full ${confColor}`} />Confidence {conf}/100</span>
                </div>
                <p className="mt-4 text-[15px] text-neutral-800 leading-relaxed">{data.scan_summary}</p>
            </div>

            {ed.length > 0 && (
                <Section title="Extracted Data" testid="scan-extracted">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                        {ed.map(([k, v]) => (
                            <div key={k} className="rounded-xl bg-neutral-50/70 border border-black/5 p-3">
                                <div className="text-[10px] font-semibold tracking-[0.1em] uppercase text-neutral-400 truncate">{k.replace(/_/g, " ")}</div>
                                <div className="text-[14px] font-medium text-black break-words">{typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {(data.business_insights || []).length > 0 && (
                <Section title="Executive Insights" testid="scan-insights">
                    <ul className="space-y-1.5">{data.business_insights.map((i, x) => <li key={x} className="text-[13.5px] text-neutral-700 flex gap-2"><span className="text-violet-500">•</span>{i}</li>)}</ul>
                </Section>
            )}

            {risks.length > 0 && (
                <Section title="Risks" testid="scan-risks" tone="rose">
                    <ul className="space-y-2">{risks.map((r, i) => (
                        <li key={i} className="text-[13px] text-rose-800">
                            <b>{typeof r === "object" ? (r.risk_title || r.title) : r}</b>
                            {typeof r === "object" && r.severity && <span className="ml-2 text-[10px] uppercase tracking-wide bg-rose-100 rounded-full px-2 py-0.5">{r.severity}</span>}
                            {typeof r === "object" && r.recommendation && <div className="text-[12.5px] text-rose-700 mt-0.5">{r.recommendation}</div>}
                        </li>
                    ))}</ul>
                </Section>
            )}

            {tasks.length > 0 && (
                <Section title="Tasks" testid="scan-tasks">
                    <div className="space-y-2">{tasks.map((t, i) => (
                        <div key={i} className="flex items-center gap-3 rounded-xl bg-neutral-50/70 border border-black/5 p-3">
                            <span className="h-6 w-6 rounded-lg bg-violet-50 text-violet-600 grid place-items-center shrink-0"><ActionIcon name="check" /></span>
                            <div className="min-w-0 flex-1"><div className="text-[13.5px] font-medium text-black">{typeof t === "object" ? t.title : t}</div>
                                {typeof t === "object" && <div className="text-[11.5px] text-neutral-400">{[t.owner_department, t.priority && `${t.priority} priority`, t.deadline].filter(Boolean).join(" · ")}</div>}</div>
                        </div>
                    ))}</div>
                </Section>
            )}

            {money.length > 0 && (
                <Section title="Money Impact" testid="scan-money">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {money.map(([k, v]) => <div key={k}><div className="text-[10px] text-neutral-400 capitalize">{k.replace(/_/g, " ")}</div><div className="text-[14px] font-medium text-neutral-800">{String(v)}</div></div>)}
                    </div>
                    {data.money_meter && (data.money_meter.before != null || data.money_meter.after != null) && (
                        <MoneyMeterDelta before={data.money_meter.before} after={data.money_meter.after} reason={data.money_meter.reason} />
                    )}
                </Section>
            )}

            {(data.decision_cards || []).length > 0 && (
                <div className="space-y-3" data-testid="scan-cards">
                    <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-600">Decision Cards</div>
                    {data.decision_cards.map((c) => <ScanCard key={c.id} card={c} onAction={onCardAction} />)}
                </div>
            )}

            {(data.recommended_next_steps || []).length > 0 && (
                <Section title="Recommended Next Steps" testid="scan-next">
                    <ul className="space-y-1.5">{data.recommended_next_steps.map((s, i) => <li key={i} className="text-[13.5px] text-neutral-700 flex gap-2"><span className="text-violet-500 font-medium">{i + 1}.</span>{s}</li>)}</ul>
                </Section>
            )}
        </div>
    );
}

function Section({ title, children, testid, tone }) {
    return (
        <div data-testid={testid} className={`rounded-3xl p-6 ${tone === "rose" ? "border border-rose-100 bg-rose-50/40" : "glass"}`}>
            <div className={`text-[10px] font-semibold tracking-[0.16em] uppercase mb-3 ${tone === "rose" ? "text-rose-500" : "text-neutral-400"}`}>{title}</div>
            {children}
        </div>
    );
}

function ScanCard({ card, onAction }) {
    const quick = (card.actions || []).filter((a) => a !== "view_details").slice(0, 3);
    return (
        <div data-testid={`scan-card-${card.id}`} className="relative glass rounded-2xl p-5 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 90% at 100% 0%, rgba(139,92,246,0.08), transparent 55%)" }} />
            <div className="relative">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-xl bg-violet-50 text-violet-600 inline-flex items-center justify-center"><ActionIcon name={CARD_ICON[card.card_type] || "flag"} /></span>
                        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-600">{card.card_label || "Decision Card"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Pill cls={PRIORITY_STYLE[card.priority] || PRIORITY_STYLE.medium}>{card.priority}</Pill>
                        {card.state && <Pill cls={STATE_STYLE[card.state] || STATE_STYLE.generated}>{prettyState(card.state)}</Pill>}
                    </div>
                </div>
                <div className="mt-3 font-display text-xl font-medium text-black">{card.title}</div>
                {card.summary && <p className="mt-1.5 text-[13px] text-neutral-600 leading-relaxed">{card.summary}</p>}
                {card.metrics?.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {card.metrics.slice(0, 3).map((mt, i) => (
                            <div key={i} className="rounded-xl bg-neutral-50/70 border border-black/5 p-2.5">
                                <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-neutral-400 truncate">{mt.label}</div>
                                <div className="text-[15px] font-medium text-black truncate">{mt.value}</div>
                            </div>
                        ))}
                    </div>
                )}
                {card.money_meter && (card.money_meter.before != null || card.money_meter.after != null) && (
                    <MoneyMeterDelta before={card.money_meter.before} after={card.money_meter.after} reason={card.money_meter.reason} />
                )}
                {card.recommended_action && <div className="mt-3 text-[12.5px] text-neutral-600"><span className="text-violet-600 font-medium">Recommended:</span> {card.recommended_action}</div>}
                {onAction && quick.length > 0 && (
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        {quick.map((a) => (
                            <button key={a} onClick={() => onAction(card, a)} data-testid={`scan-card-action-${a}-${card.id}`}
                                className={`text-[12.5px] font-medium px-3.5 py-1.5 rounded-full transition-colors ${PRIMARY_ACTIONS.has(a) ? "bg-black text-white hover:bg-neutral-800" : DANGER_ACTIONS.has(a) ? "bg-white text-rose-600 border border-rose-200 hover:bg-rose-50" : "bg-white text-black border border-black/10 hover:bg-neutral-50"}`}>
                                {ACTION_LABEL[a] || a}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Pill({ cls, children }) {
    return <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.1em] uppercase rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}
